import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { XIcon, PlusIcon } from "lucide-react";
import { Service } from "../SelectSpecs";
import {
  Select,
  SelectTrigger,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectValue,
} from "@/components/ui/select";
import { DefaultSpecs } from "../DefaultSpecs/DefaultSpecs";

type Spec = Service["specs"][number];
export interface Deliverable {
  formatId?: string;
  specId?: string;
  custom?: boolean;
  formatName?: string;
  specName?: string;
  addEmptyDeliverableRow?: () => void;
}

interface Props {
  deliverable: Deliverable;
  setDeliverable: (deliverable: Deliverable) => void;
  removeDeliverable: (deliverable?: Deliverable) => void;
  specOptions: Spec[];
  showRemoveButton: boolean;
  addEmptyDeliverableRow?: () => void;
  removeEmptyDeliverableRow?: () => void;
  deliverableValue: Deliverable[];
  projectId: string;
  serviceId: string;
  editSpecs?: boolean;
  disabled?: boolean;
}

const DeliverableRow = ({
  deliverable,
  specOptions,
  setDeliverable,
  removeDeliverable,
  showRemoveButton,
  addEmptyDeliverableRow,
  removeEmptyDeliverableRow,
  deliverableValue,
  projectId,
  serviceId,
  editSpecs = false,
  disabled = false,
}: Props) => {
  const spec = deliverable
    ? specOptions.find((option) => option.id === deliverable.specId)
    : null;

  return (
    <fieldset>
      <div
        className={editSpecs ? "flex gap-2" : "flex gap-[20px] items-center"}
      >
        <div
          className={
            editSpecs
              ? "!flex gap-2 items-center"
              : "flex gap-[20px] items-center"
          }
        >
          <Select
            value={deliverable ? deliverable.specId : undefined}
            onValueChange={(val) => {
              if (!val) {
                return;
              }
              const selectedSpec = specOptions.find(
                (option) => option.id === val,
              );
              if (selectedSpec) {
                setDeliverable({
                  specId: selectedSpec.id,
                  specName: selectedSpec.name,
                });
              } else {
                setDeliverable({ custom: true });
              }
            }}
            disabled={disabled}
          >
            <SelectTrigger className="w-50 border-none !bg-tertiary text-primary-foreground">
              <SelectValue placeholder="Deliverable" />
            </SelectTrigger>
            <SelectContent className="w-50">
              <SelectGroup>
                {specOptions.map((option) => (
                  <SelectItem
                    key={option.id}
                    value={option.id}
                    className="hover:bg-accent-foreground"
                  >
                    {option.name}
                  </SelectItem>
                ))}
              </SelectGroup>
            </SelectContent>
          </Select>

          {deliverable && deliverable.custom ? (
            <div>
              <Input
                type="text"
                value={deliverable.specName || ""}
                placeholder="Specification"
                onChange={(e) =>
                  setDeliverable({
                    ...deliverable,
                    specName: e.target.value,
                  })
                }
              />
              <Input
                type="text"
                placeholder="Format"
                value={deliverable.formatName || ""}
                onChange={(e) =>
                  setDeliverable({
                    ...deliverable,
                    formatName: e.target.value,
                  })
                }
              />
            </div>
          ) : (
            <Select
              value={deliverable ? deliverable.formatId : undefined}
              onValueChange={(val) => {
                if (!val || !spec || !spec.formats) {
                  return;
                }
                const selectedFormat = spec.formats.find(
                  (format) => (format && format.id) === val,
                );
                if (selectedFormat) {
                  setDeliverable({
                    ...deliverable,
                    formatId: selectedFormat.id,
                    formatName: selectedFormat.format,
                  });
                }
              }}
              disabled={disabled}
            >
              <SelectTrigger className="w-50 border-none !bg-tertiary text-primary-foreground">
                <SelectValue placeholder="Format" />
              </SelectTrigger>
              <SelectContent className="w-50">
                <SelectGroup>
                  {spec && spec.formats
                    ? spec.formats.map((format) =>
                        format ? (
                          <SelectItem
                            key={format.id}
                            value={format.id}
                            className="hover:bg-accent-foreground"
                          >
                            {format.format}
                          </SelectItem>
                        ) : null,
                      )
                    : null}
                </SelectGroup>
              </SelectContent>
            </Select>
          )}
        </div>
        {showRemoveButton ? (
          <Button
            className="border border-accent-foreground !p-2 hover:bg-accent cursor-pointer"
            onClick={() => {
              if (deliverable) {
                removeDeliverable(deliverable);
              } else if (removeEmptyDeliverableRow) {
                removeEmptyDeliverableRow();
              }
            }}
          >
            <XIcon className="!h-[14px] !w-[14px]" />
          </Button>
        ) : (
          <div
            className={
              editSpecs ? "flex gap-2" : "flex gap-[20px] items-center"
            }
          >
            <Button
              className="!p-2 cursor-pointer h-full rounded-sm border border-border"
              onClick={addEmptyDeliverableRow}
            >
              <PlusIcon className="!h-[13px] !w-[13px]" />
            </Button>
            {deliverableValue && (
              <DefaultSpecs
                projectId={projectId}
                serviceId={serviceId}
                specsList={deliverableValue.map((dil) => {
                  return {
                    formatId: dil.formatId as string,
                    specId: dil.specId as string,
                  };
                })}
              />
            )}
          </div>
        )}
      </div>
    </fieldset>
  );
};

export default DeliverableRow;
